/**
 * Busca de negócios pela Edge `search` (T2.9).
 *
 * A Edge é a única porta da busca completa: aplica pesos uniformes, rate limit e cursor opaco, e
 * devolve a explicação do score (🔴 C7-a). Este módulo só invoca, repassa o cancelamento e valida o
 * contrato — inclusive a invariante do MVP `sponsored: false`: uma resposta que diga o contrário é
 * tratada como fora do contrato, nunca exibida.
 *
 * Erros são lançados como `SearchDataError` com `code` tipado, no padrão de `DiscoveryDataError`.
 */
import type { SearchRequestInput } from '@powerful/types';

export interface SearchBusiness {
  id: string;
  slug: string;
  name: string;
  city: string | null;
  categoryId: string | null;
  coverUrl: string | null;
  logoUrl: string | null;
  distanceKm: number | null;
  ratingAvg: number;
  ratingCount: number;
  minPriceCents: number | null;
  hasAddons: boolean;
  bookingEnabled: boolean;
  sealStatus: string;
  badges: string[];
  isFavorite: boolean;
}

export interface SearchRankingFactor {
  key: 'distance' | 'rating' | 'plan' | 'recency';
  weight: number;
  value: number;
  contribution: number;
}

export interface SearchResult {
  business: SearchBusiness;
  ranking: {
    score: number;
    factors: SearchRankingFactor[];
    commercial: { planInfluenced: boolean; contribution: number };
  };
}

export interface SearchResponse {
  results: SearchResult[];
  nextCursor: string | null;
  weights: { distance: number; rating: number; plan: number; recency: number; source: 'flag' | 'default'; version: string };
  sponsored: false;
  total: null;
  cursorReset?: true;
}

export type SearchErrorCode =
  | 'unauthenticated'
  | 'account_not_active'
  | 'invalid_request'
  | 'invalid_cursor'
  | 'cursor_mismatch'
  | 'rate_limited'
  | 'invalid_response'
  | 'aborted'
  | 'network'
  | 'unknown';

export interface SearchValidationIssue {
  path: Array<string | number>;
  key: string;
  values?: Record<string, number>;
}

export class SearchDataError extends Error {
  readonly code: SearchErrorCode;
  readonly retryAfterSeconds?: number;
  readonly issues?: SearchValidationIssue[];

  constructor(
    code: SearchErrorCode,
    extra: { retryAfterSeconds?: number; issues?: SearchValidationIssue[]; cause?: unknown } = {},
  ) {
    super(code, { cause: extra.cause });
    this.name = 'SearchDataError';
    this.code = code;
    this.retryAfterSeconds = extra.retryAfterSeconds;
    this.issues = extra.issues;
  }
}

export interface SearchClient {
  functions: {
    invoke(
      name: string,
      options: { body: SearchRequestInput; signal?: AbortSignal },
    ): PromiseLike<{ data: unknown; error: unknown }>;
  };
}

const KNOWN_CODES: SearchErrorCode[] = [
  'unauthenticated',
  'account_not_active',
  'invalid_request',
  'invalid_cursor',
  'cursor_mismatch',
  'rate_limited',
];

async function fromHttpError(error: unknown): Promise<SearchDataError> {
  const response = (error as { context?: unknown })?.context;
  if (!(response instanceof Response)) {
    return new SearchDataError('network', { cause: error });
  }
  let body: { error?: { code?: unknown; issues?: SearchValidationIssue[] } } = {};
  try {
    body = (await response.clone().json()) as typeof body;
  } catch {
    // Corpo não-JSON (gateway): cai para o código pelo status.
  }
  const raw = body.error?.code;
  const code = KNOWN_CODES.includes(raw as SearchErrorCode)
    ? (raw as SearchErrorCode)
    : response.status === 401
      ? 'unauthenticated'
      : 'unknown';
  const retry = Number(response.headers.get('retry-after'));
  return new SearchDataError(code, {
    cause: error,
    ...(code === 'rate_limited' && Number.isFinite(retry) ? { retryAfterSeconds: retry } : {}),
    ...(code === 'invalid_request' && Array.isArray(body.error?.issues) ? { issues: body.error.issues } : {}),
  });
}

function isSearchResponse(data: unknown): data is SearchResponse {
  const d = data as Partial<SearchResponse> | null;
  return (
    !!d &&
    d.sponsored === false &&
    Array.isArray(d.results) &&
    (d.nextCursor === null || typeof d.nextCursor === 'string') &&
    !!d.weights &&
    d.results.every(
      (r) =>
        !!r &&
        typeof r.business?.id === 'string' &&
        typeof r.business?.slug === 'string' &&
        typeof r.business?.name === 'string' &&
        typeof r.ranking?.score === 'number' &&
        Array.isArray(r.ranking?.factors) &&
        typeof r.ranking?.commercial?.planInfluenced === 'boolean',
    )
  );
}

export async function searchBusinesses(
  client: SearchClient,
  input: SearchRequestInput,
  signal: AbortSignal = new AbortController().signal,
): Promise<SearchResponse> {
  let result: { data: unknown; error: unknown };
  try {
    result = await client.functions.invoke('search', { body: input, signal });
  } catch (cause) {
    throw new SearchDataError(signal.aborted ? 'aborted' : 'network', { cause });
  }
  if (result.error) {
    if (signal.aborted) throw new SearchDataError('aborted', { cause: result.error });
    throw await fromHttpError(result.error);
  }
  if (!isSearchResponse(result.data)) throw new SearchDataError('invalid_response');
  return result.data;
}
